package org.teddapp.project.ui.skills

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Assistant_conversation
import com.tedd.db.Skill_resource
import kotlinx.coroutines.launch
import org.teddapp.project.resources.ResourceRules
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddDates
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.projects.MenuAction
import org.teddapp.project.ui.projects.ProjectMoreMenu
import org.teddapp.project.ui.rememberAfterFrame

/**
 * L'assistant d'une compétence : ses conversations et celle qui est ouverte. Sur grand
 * écran, la liste à gauche et la conversation à droite ; sur téléphone, l'une puis l'autre.
 */
@Composable
internal fun AssistantView(model: AssistantModel, skillName: String, compact: Boolean, onClose: () -> Unit, modifier: Modifier = Modifier) {
    Box(modifier.fillMaxSize().background(TeddTokens.Bg)) {
        if (compact) {
            if (model.selectedId == null) {
                ConversationList(model, skillName, compact = true, onClose = onClose, modifier = Modifier.fillMaxSize().padding(16.dp))
            } else {
                ConversationPane(model, compact = true, onBack = model::close, modifier = Modifier.fillMaxSize().padding(horizontal = 16.dp, vertical = 12.dp))
            }
        } else {
            Row(Modifier.fillMaxSize()) {
                org.teddapp.project.ui.TeddSidePane(
                    spec = CONVERSATIONS_PANE,
                    title = "la liste des conversations",
                    collapsedContent = { expand ->
                        Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.fillMaxSize()) {
                            org.teddapp.project.ui.TeddIconButton(onClick = model::newConversation, modifier = Modifier.padding(top = 12.dp)) {
                                TeddIcon(name = "plus", size = 16.dp, color = TeddTokens.Ink)
                            }
                            org.teddapp.project.ui.TeddPaneRail(
                                expand = expand,
                                title = "la liste des conversations",
                                items = model.visible.map { c ->
                                    org.teddapp.project.ui.PaneRailItem(c.id, model.titleOf(c), "spark", selected = c.id == model.selectedId) { model.open(c.id) }
                                },
                            )
                        }
                    },
                ) {
                    ConversationList(
                        model, skillName, compact = false, onClose = onClose,
                        modifier = Modifier.fillMaxSize(),
                    )
                }
                if (model.selectedId == null) {
                    Box(Modifier.weight(1f).fillMaxHeight(), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.widthIn(max = 420.dp)) {
                            TeddIcon(name = "spark", size = 28.dp, color = TeddTokens.InkMuted)
                            Text("Pose tes questions sur les ressources de « $skillName ».", style = TeddType.BodyLg, color = TeddTokens.InkMuted)
                            TeddButton(text = "Nouvelle conversation", onClick = model::newConversation, kind = TeddButtonKind.Primary, icon = "plus")
                        }
                    }
                } else {
                    ConversationPane(model, compact = false, onBack = null, modifier = Modifier.weight(1f).fillMaxHeight().padding(horizontal = 28.dp, vertical = 16.dp))
                }
            }
        }
        model.renaming?.let { RenameConversationDialog(model) }
    }
}

/** La liste des conversations : 320 dp par défaut, de 240 à 480. */
private val CONVERSATIONS_PANE = org.teddapp.project.ui.PaneSpec("assistant.conversations", default = 320.dp, min = 240.dp, max = 480.dp)

@Composable
private fun NoticeBar(model: AssistantModel) {
    val text = model.notice ?: return
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        modifier = Modifier.fillMaxWidth().background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm)).padding(horizontal = 12.dp, vertical = 10.dp),
    ) {
        Text(text, style = TeddType.Body, color = TeddTokens.Ink, modifier = Modifier.weight(1f))
        if (model.undoable != null) {
            TeddButton(text = "Annuler", onClick = model::undo, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "undo")
        }
        // Sans modèle réglé, ou avec un modèle qui refuse : le chemin le plus court vers les réglages.
        val openSettings = org.teddapp.project.ui.settings.LocalOpenSettings.current
        if (openSettings != null && AssistantTexts.sendsToSettings(text)) {
            TeddButton(text = "Réglages", onClick = { model.notice = null; openSettings() }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "settings")
        }
        Text(
            "Fermer", style = TeddType.Caption, color = TeddTokens.InkMuted,
            modifier = Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { model.notice = null; model.undoable = null },
        )
    }
}

private fun day(ms: Long?): String? = ms?.let { TeddDates.formatShort(TeddDates.isoFromDays((it / 86_400_000L).toInt()), withYear = false) }

@Composable
private fun ConversationList(model: AssistantModel, skillName: String, compact: Boolean, onClose: () -> Unit, modifier: Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            TeddButton(text = "Fiche", onClick = onClose, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "arrow-left")
            Spacer(Modifier.weight(1f))
            TeddButton(text = "Nouvelle", onClick = model::newConversation, kind = TeddButtonKind.Primary, size = TeddButtonSize.Sm, icon = "plus")
        }
        Column {
            Text("ASSISTANT", style = TeddType.Overline, color = TeddTokens.InkMuted)
            Text(skillName, style = TeddType.Title2, color = TeddTokens.Ink, maxLines = 2, overflow = TextOverflow.Ellipsis)
        }
        if (model.conversations.size > 4 || model.search.isNotEmpty()) {
            TeddField(label = "Rechercher", value = model.search, onValueChange = { model.search = it }, placeholder = "Un titre de conversation")
        }
        if (model.selectedId == null || compact) NoticeBar(model)
        val list = model.visible
        LazyColumn(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            if (model.conversations.isEmpty()) item {
                Text(
                    "Aucune conversation. Chacune garde son historique, sur tous tes appareils.",
                    style = TeddType.Body, color = TeddTokens.InkMuted, modifier = Modifier.padding(vertical = 8.dp),
                )
            } else if (list.isEmpty()) item {
                Text("Aucun titre ne correspond.", style = TeddType.Body, color = TeddTokens.InkMuted)
            }
            items(list, key = { it.id }) { c -> ConversationRow(model, c, selected = c.id == model.selectedId) }
            if (model.trash.isNotEmpty()) item { ConversationTrash(model) }
        }
    }
}

@Composable
private fun ConversationRow(model: AssistantModel, c: Assistant_conversation, selected: Boolean) {
    val shape = RoundedCornerShape(TeddTokens.RMd)
    Row(
        verticalAlignment = Alignment.Top,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        modifier = Modifier
            .fillMaxWidth()
            .clip(shape)
            .background(if (selected) TeddTokens.SurfaceSunken else TeddTokens.Bg, shape)
            .clickable { model.open(c.id) }
            .padding(start = 12.dp, top = 10.dp, bottom = 10.dp, end = 4.dp),
    ) {
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(
                    model.titleOf(c), style = TeddType.BodyStrong,
                    color = if (c.title.isNullOrBlank()) TeddTokens.InkMuted else TeddTokens.Ink,
                    maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f),
                )
                day(c.last_message_at ?: c.created_at)?.let { Text(it, style = TeddType.Caption, color = TeddTokens.InkSubtle) }
            }
            val preview = when {
                c.push_error != null -> AssistantTexts.errorMessage("conversation_refused")
                else -> model.lastAnswers[c.id]
            }
            preview?.let {
                Text(it, style = TeddType.Caption, color = if (c.push_error != null) TeddTokens.Danger else TeddTokens.InkMuted, maxLines = 2, overflow = TextOverflow.Ellipsis)
            }
        }
        ProjectMoreMenu(
            listOf(
                MenuAction("Renommer", "edit", { model.startRename(c) }),
                MenuAction("Supprimer", "trash", { model.delete(c) }, danger = true),
            ),
            size = 28.dp,
        )
    }
}

@Composable
private fun ConversationTrash(model: AssistantModel) {
    var open by remember { mutableStateOf(false) }
    Column(verticalArrangement = Arrangement.spacedBy(6.dp), modifier = Modifier.padding(top = 8.dp)) {
        Text(
            (if (open) "▾ " else "▸ ") + "Récemment supprimées · ${model.trash.size}",
            style = TeddType.Caption, color = TeddTokens.InkMuted,
            modifier = Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { open = !open },
        )
        if (open) model.trash.forEach { c ->
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.padding(start = 12.dp)) {
                Text(model.titleOf(c), style = TeddType.Body, color = TeddTokens.InkMuted, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
                TeddButton(text = "Restaurer", onClick = { model.restore(c) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "undo")
            }
        }
    }
}

@Composable
private fun ConversationPane(model: AssistantModel, compact: Boolean, onBack: (() -> Unit)?, modifier: Modifier) {
    val c = model.selected ?: return
    var opened by remember { mutableStateOf<Pair<Source, Skill_resource?>?>(null) }
    Column(modifier, verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            // Sur téléphone, une flèche suffit : le titre garde la place.
            onBack?.let {
                org.teddapp.project.ui.TeddIconButton(onClick = it) { TeddIcon(name = "arrow-left", size = 18.dp, color = TeddTokens.Ink) }
            }
            Text(model.titleOf(c), style = TeddType.Title2, color = TeddTokens.Ink, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
            ProjectMoreMenu(
                listOf(
                    MenuAction("Renommer", "edit", { model.startRename(c) }),
                    MenuAction("Supprimer", "trash", { model.delete(c) }, danger = true),
                ),
                size = 28.dp,
            )
        }
        NoticeBar(model)
        val shown = model.shown
        val listState = rememberLazyListState()
        // La dernière réponse reste en vue pendant qu'elle s'écrit.
        LaunchedEffect(shown.size, shown.lastOrNull()?.text?.length) {
            if (shown.isNotEmpty()) listState.scrollToItem(shown.lastIndex)
        }
        LazyColumn(Modifier.weight(1f).fillMaxWidth(), state = listState, verticalArrangement = Arrangement.spacedBy(14.dp)) {
            if (shown.isEmpty()) item {
                Text(
                    "Pose une question : l'assistant répond à partir des ressources prêtes de la compétence, et cite ses sources.",
                    style = TeddType.BodyLg, color = TeddTokens.InkMuted, modifier = Modifier.padding(top = 24.dp),
                )
            }
            items(shown, key = { it.id }) { m -> MessageBlock(model, m, compact) { source, resource -> opened = source to resource } }
        }
        Composer(model)
    }
    opened?.let { (source, resource) -> SourceDialog(source, resource) { opened = null } }
}

@Composable
private fun MessageBlock(model: AssistantModel, m: ShownMessage, compact: Boolean, onOpen: (Source, Skill_resource?) -> Unit) {
    if (m.fromUser) {
        Row(Modifier.fillMaxWidth()) {
            Spacer(Modifier.weight(if (compact) 0.15f else 0.3f))
            SelectionContainer {
                Text(
                    m.text, style = TeddType.BodyLg, color = TeddTokens.Ink,
                    modifier = Modifier.background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RMd)).padding(horizontal = 14.dp, vertical = 10.dp),
                )
            }
        }
        return
    }
    Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            TeddIcon(name = "spark", size = 14.dp, color = TeddTokens.InkMuted)
            Text("Assistant", style = TeddType.Caption, color = TeddTokens.InkMuted)
        }
        if (m.text.isNotEmpty()) SelectionContainer { Text(m.text, style = TeddType.BodyLg, color = TeddTokens.Ink) }
        else if (m.status == "streaming") Text("…", style = TeddType.BodyLg, color = TeddTokens.InkMuted)
        m.note?.let {
            Text(it, style = TeddType.Caption, color = if (m.status == "failed") TeddTokens.Danger else TeddTokens.InkMuted)
        }
        if (m.sources.isNotEmpty()) SourcesBlock(model, m.sources, onOpen)
    }
}

@Composable
private fun SourcesBlock(model: AssistantModel, sources: List<Source>, onOpen: (Source, Skill_resource?) -> Unit) {
    // Une source dont la ressource a disparu de l'appareil est dite supprimée.
    var resources by remember(sources) { mutableStateOf<Map<String, Skill_resource?>?>(null) }
    LaunchedEffect(sources) { resources = sources.associate { it.resourceId to model.resourceOf(it) } }
    val uri = LocalUriHandler.current
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text("SOURCES", style = TeddType.Overline, color = TeddTokens.InkSubtle)
        sources.forEach { s ->
            val known = resources
            val resource = known?.get(s.resourceId)
            val deleted = known != null && resource == null
            TeddCard(
                modifier = Modifier.fillMaxWidth().clickable(enabled = !deleted) {
                    val url = resource?.url
                    if (resource?.origin == "link" && url != null) runCatching { uri.openUri(url) } else onOpen(s, resource)
                },
                padding = PaddingValues(horizontal = 12.dp, vertical = 8.dp),
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text(s.title, style = TeddType.Label, color = if (deleted) TeddTokens.InkMuted else TeddTokens.Ink, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f, fill = false))
                        s.where?.let { Text(it, style = TeddType.Caption, color = TeddTokens.InkMuted) }
                        if (deleted) Text("source supprimée", style = TeddType.Caption, color = TeddTokens.InkSubtle)
                    }
                    if (s.excerpt.isNotBlank()) Text("« ${s.excerpt} »", style = TeddType.Caption, color = TeddTokens.InkMuted, maxLines = 2, overflow = TextOverflow.Ellipsis)
                }
            }
        }
    }
}

@Composable
private fun SourceDialog(source: Source, resource: Skill_resource?, onClose: () -> Unit) {
    val close = rememberAfterFrame(onClose)
    Dialog(onDismissRequest = close, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            Modifier.widthIn(max = 560.dp).fillMaxWidth().padding(16.dp).heightIn(max = 640.dp)
                .background(TeddTokens.SurfaceOverlay, RoundedCornerShape(TeddTokens.RLg))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RLg))
                .verticalScroll(rememberScrollState()).padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Text(resource?.title ?: source.title, style = TeddType.Title2, color = TeddTokens.Ink)
            Text(
                listOfNotNull(resource?.let { ResourceRules.formatLabel(it.origin, it.format) }, source.where).joinToString(" · "),
                style = TeddType.Caption, color = TeddTokens.InkMuted,
            )
            if (source.excerpt.isNotBlank()) Text("« ${source.excerpt} »", style = TeddType.BodyLg, color = TeddTokens.Ink)
            resource?.body?.takeIf { it.isNotBlank() }?.let {
                Text("TEXTE DE LA RESSOURCE", style = TeddType.Overline, color = TeddTokens.InkSubtle)
                SelectionContainer { Text(it.take(4_000) + if (it.length > 4_000) "…" else "", style = TeddType.Body, color = TeddTokens.InkMuted) }
            }
            if (resource?.origin == "file") Text("Le fichier est dans la section Ressources de la compétence.", style = TeddType.Caption, color = TeddTokens.InkMuted)
            TeddButton(text = "Fermer", onClick = close, kind = TeddButtonKind.Secondary)
        }
    }
}

@Composable
private fun Composer(model: AssistantModel) {
    Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
        Box(
            Modifier.weight(1f).heightIn(min = 44.dp, max = 180.dp).clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RMd)).padding(horizontal = 12.dp, vertical = 12.dp),
        ) {
            BasicTextField(
                value = model.draft,
                onValueChange = { model.draft = it.take(AssistantTexts.QUESTION_MAX) },
                textStyle = TeddType.Body.copy(color = TeddTokens.Ink, fontSize = 14.sp),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                modifier = Modifier.fillMaxWidth(),
                decorationBox = { inner ->
                    if (model.draft.isEmpty()) Text("Ta question…", color = TeddTokens.InkSubtle, fontSize = 14.sp)
                    inner()
                },
            )
        }
        if (model.asking) {
            TeddButton(text = "Arrêter", onClick = model::stop, kind = TeddButtonKind.Secondary, icon = "x")
        } else {
            TeddButton(text = "Envoyer", onClick = model::send, kind = TeddButtonKind.Primary, icon = "send", enabled = model.draft.isNotBlank())
        }
    }
}

@Composable
private fun RenameConversationDialog(model: AssistantModel) {
    val close = rememberAfterFrame { model.renaming = null }
    Dialog(onDismissRequest = close, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            Modifier.widthIn(max = 480.dp).fillMaxWidth().padding(16.dp)
                .background(TeddTokens.SurfaceOverlay, RoundedCornerShape(TeddTokens.RLg))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RLg)).padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text("Renommer la conversation", style = TeddType.Title2, color = TeddTokens.Ink)
            TeddField(label = "Titre", value = model.renameText, onValueChange = { model.renameText = it })
            model.renameError?.let { Text(it, style = TeddType.Body, color = TeddTokens.Danger) }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                TeddButton(text = "Enregistrer", onClick = model::confirmRename, kind = TeddButtonKind.Primary, modifier = Modifier.weight(1f))
                TeddButton(text = "Annuler", onClick = close, kind = TeddButtonKind.Ghost)
            }
        }
    }
}

/** L'assistant de la compétence, branché sur la base et la session : à poser à la place de la fiche. */
@Composable
internal fun AssistantScreen(repos: org.teddapp.project.db.TeddRepositories, skillId: String, skillName: String, compact: Boolean, onClose: () -> Unit, modifier: Modifier = Modifier) {
    val bindings = org.teddapp.project.ui.settings.LocalSyncBindings.current
    val model = rememberAssistantModel(repos.assistant, repos.db, skillId, org.teddapp.project.server.LocalServerSession.current) { bindings?.onSyncNow() }
    AssistantView(model, skillName, compact, onClose, modifier)
}

/** L'assistant seul, avec une conversation ouverte : pour les captures d'écran. */
@Composable
fun AssistantPanel(repos: org.teddapp.project.db.TeddRepositories, skillId: String, skillName: String, compact: Boolean, openConversation: String? = null) {
    val model = rememberAssistantModel(repos.assistant, repos.db, skillId, null) {}
    LaunchedEffect(openConversation) { openConversation?.let(model::open) }
    AssistantView(model, skillName, compact, onClose = {})
}
